import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, ChevronDown, Sparkles, CircleHelp } from "lucide-react";

const toDisplayLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getSampleLabel = (sample) => {
  if (sample === null || sample === undefined || sample === "") return "--";
  return String(sample);
};

const MappingSelect = ({
  value,
  options = [],
  placeholder = "Select attribute...",
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        (!menuRef.current || !menuRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || !wrapperRef.current) {
      return undefined;
    }

    const updateMenuPosition = () => {
      if (!wrapperRef.current) {
        return;
      }

      const rect = wrapperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const estimatedMenuHeight = Math.min(280, 48 + options.length * 42);
      const spaceBelow = viewportHeight - rect.bottom;
      const openUpward =
        spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;

      setMenuPosition({
        left: rect.left,
        width: rect.width,
        top: openUpward ? rect.top - 8 : rect.bottom + 8,
        openUpward,
      });
    };

    updateMenuPosition();

    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, options.length]);

  const selectedLabel = value ? toDisplayLabel(value) : placeholder;

  const dropdownMenu =
    isOpen && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed z-[9999] overflow-hidden rounded-2xl border border-[#C7DFD9] bg-white shadow-[0_16px_32px_rgba(6,81,61,0.12)]"
            style={{
              left: menuPosition.left,
              width: menuPosition.width,
              top: menuPosition.openUpward ? "auto" : menuPosition.top,
              bottom: menuPosition.openUpward
                ? window.innerHeight - menuPosition.top
                : "auto",
            }}
          >
            <button
              type="button"
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
              className={`block w-full px-4 py-2.5 text-left text-[13px] transition hover:bg-[#EEF6F3] focus:bg-[#EEF6F3] focus:outline-none ${
                !value
                  ? "bg-[#E6EEEC] font-semibold text-[#06513D]"
                  : "text-[#667085]"
              }`}
            >
              {placeholder}
            </button>

            <div className="max-h-56 overflow-y-auto py-1">
              {options.map((option) => {
                const isSelected = option === value;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                    }}
                    className={`block w-full px-4 py-2.5 text-left text-[13px] transition hover:bg-[#EEF6F3] focus:bg-[#EEF6F3] focus:outline-none ${
                      isSelected
                        ? "bg-[#E6EEEC] font-semibold text-[#06513D]"
                        : "text-[#344054]"
                    }`}
                  >
                    {toDisplayLabel(option)}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex h-10 w-full items-center justify-between rounded-full border bg-white px-4 pr-14 text-left text-[13px] font-medium outline-none transition ${
          isOpen
            ? "border-[#06513D] ring-2 ring-[#06513D]/10"
            : "border-[#06513D]"
        } ${value ? "text-[#344054]" : "text-[#667085]"}`}
      >
        <span className="truncate">{selectedLabel}</span>
        <ChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#667085] transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {dropdownMenu}
    </div>
  );
};

const LocationMapping = ({
  mappingRows = [],
  mappingSelections = {},
  attributeOptions = [],
  onSelectionChange,
  onCancel,
  onImport,
  importResult,
  isImporting = false,
  validationError = "",
  importButtonLabel = "Import Locations",
  backButtonLabel = "Back",
}) => {
  const mappedCount = useMemo(() => {
    return mappingRows.filter((row) => !!mappingSelections[row.header]).length;
  }, [mappingRows, mappingSelections]);

  const totalCount = mappingRows.length;
  const hasRows = totalCount > 0;
  const allMapped = hasRows && mappedCount === totalCount;
  const importPayload = importResult?.data || {};
  const importValidationErrors = Array.isArray(importPayload?.validationErrors)
    ? importPayload.validationErrors
    : [];
  const canImport = true;

  const handleDownloadFailedCsv = () => {
    const csvData = importPayload?.failedImportCsv;
    const filename = importPayload?.failedImportFilename || "failed-import.csv";
    if (!csvData) return;

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-[24px] border border-[#06513D] bg-white p-4 sm:p-6">
      <div className="border-b border-[#EAECF0] pb-5">
        <div className="flex items-start gap-2 text-[#06513d]">
          <CircleHelp className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <h3 className="text-[15px] font-semibold text-[#06513d]">
              Mapping data
            </h3>
            <p className="mt-1 text-[12px] leading-5 text-[#667085]">
              Select the attribute that corresponds to your data. You can select
              existing attributes, create new ones, or choose not to import some
              data.
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-start justify-between gap-3 rounded-2xl border border-[#06513D] bg-white px-4 py-3 shadow-sm">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#06513D]" />
            <p className="text-[12px] leading-5 text-[#06513d]">
              Based on the imported data, suggested mappings are already
              selected. Review them before importing.
            </p>
          </div>
        </div>
      </div>

      {hasRows && (
        <div className="mt-5 hidden grid-cols-[1.1fr_1.4fr_1.4fr_.7fr] gap-6 px-3 md:grid">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#98A2B3]">
            File Header
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#98A2B3]">
            Data
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#98A2B3]">
            Attribute
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#98A2B3]">
            Mapped
          </span>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {!hasRows ? (
          <div className="rounded-2xl border border-dashed border-[#D0D5DD] bg-[#FCFCFD] px-4 py-8 text-center">
            <p className="text-[14px] font-medium text-[#344054]">
              No mapping rows available
            </p>
            <p className="mt-1 text-[12px] text-[#98A2B3]">
              Upload and validate a file to see detected headers.
            </p>
          </div>
        ) : (
          mappingRows.map((row) => {
            const selectedAttribute = mappingSelections[row.header] || "";
            const isMapped = !!selectedAttribute;
            const samples = Array.isArray(row.samples) ? row.samples : [];

            return (
              <div
                key={row.header}
                className="rounded-2xl border border-[#06513D] bg-[#F8FBFA] px-4 py-4 sm:px-5"
              >
                <div className="grid gap-4 md:grid-cols-[1.1fr_1.4fr_1.4fr_.7fr] md:items-start md:gap-6">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#98A2B3] md:hidden">
                      File Header
                    </p>
                    <p className="mt-1 text-[13px] font-semibold uppercase text-[#344054] md:mt-0">
                      {row.header}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#98A2B3] md:hidden">
                      Data
                    </p>
                    <div className="mt-1 space-y-1 text-[12px] leading-5 text-[#344054] md:mt-0">
                      {samples.length > 0 ? (
                        samples
                          .slice(0, 3)
                          .map((sample, index) => (
                            <p key={`${row.header}_sample_${index}`}>
                              {getSampleLabel(sample)}
                            </p>
                          ))
                      ) : (
                        <p>--</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#98A2B3] md:hidden">
                      Attribute
                    </p>
                    <div className="mt-1 md:mt-0">
                      <MappingSelect
                        value={selectedAttribute}
                        options={attributeOptions}
                        onChange={(nextValue) =>
                          onSelectionChange(row.header, nextValue)
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#98A2B3] md:hidden">
                      Mapped
                    </p>
                    <div className="mt-1 flex items-center md:mt-0 md:justify-center">
                      {isMapped ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F7EF] px-2.5 py-1 text-[11px] font-semibold text-[#067647]">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#98A2B3]">
                          --
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {(importValidationErrors.length > 0 ||
        importPayload?.failedCount > 0) && (
        <div className="mt-4 rounded-2xl border border-[#06513d] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#22513d]">
                Rows Not Imported
              </p>
              <p className="mt-1 text-xs text-[#22513d]">
                {importResult?.message || "Some rows could not be imported."}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2 text-xs ">
              <div>
                <span className="text-[#22513d]">
                  Total: {importPayload?.totalRows ?? 0}
                </span>
                <span className="mx-2 text-[#22513d]">|</span>
                <span>Imported: {importPayload?.importedCount ?? 0}</span>
                <span className="mx-2 text-[#22513d]">|</span>
                <span className="mx-2 text-[#B42318]">
                  Failed: {importPayload?.failedCount ?? 0}
                </span>
              </div>
              {importPayload?.failedCount > 0 &&
                importPayload?.failedImportCsv && (
                  <button
                    type="button"
                    onClick={handleDownloadFailedCsv}
                    className="rounded border border-[#22513d] bg-white px-3 py-1.5 text-[11px] font-semibold text-[#22513d] transition hover:bg-[#F8FBFA]"
                  >
                    Download Failed Report
                  </button>
                )}
            </div>
          </div>

          {/* <div className="mt-4 space-y-3">
            {importValidationErrors.map((item, index) => {
              const rowErrors = Array.isArray(item?.errors) ? item.errors : [];

              return (
                <div
                  key={`import_error_row_${item?.rowIndex || index}`}
                  className="rounded-2xl border border-[#FCA5A5] bg-white px-4 py-3"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    {item?.name ? (
                      <span className="text-sm font-semibold text-[#344054]">
                        {item.name}
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-3 space-y-2">
                    {rowErrors.map((errorText, errorIndex) => (
                      <p
                        key={`row_${item?.rowIndex || index}_error_${errorIndex}`}
                        className="text-sm leading-6 text-[#7A271A]"
                      >
                        {errorText}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div> */}
        </div>
      )}

      <div className="mt-4 border-t border-[#EAECF0] pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[12px] text-[#667085]">
            {allMapped
              ? "All columns are mapped and ready to import."
              : `${totalCount - mappedCount} column(s) still need attention.`}
          </p>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="h-9 rounded-lg border border-[#D0D5DD] bg-white px-4 text-[13px] font-semibold text-[#344054] transition hover:bg-[#F9FAFB]"
            >
              {backButtonLabel}
            </button>

            <button
              type="button"
              onClick={onImport}
              disabled={!canImport || isImporting}
              className={`h-9 rounded-lg px-4 text-[13px] font-semibold text-white transition ${
                !canImport || isImporting
                  ? "cursor-not-allowed bg-[#98A2B3]"
                  : " bg-[#06513D] hover:!bg-[#387464]"
              }`}
            >
              {isImporting ? "Importing..." : importButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationMapping;
